import { z } from 'zod';
/**
 * Жизненный цикл устройства (DEVICE_ENROLLMENT.md):
 *
 *   pending → approved → revoked
 *      └────→ blocked
 *
 * Доступ к функциональным API имеет только `approved` устройство
 * с действующей server-side сессией.
 */
export const deviceStatusSchema = z.enum(['pending', 'approved', 'blocked', 'revoked']);
export const devicePlatformSchema = z.enum(['web', 'android-tv']);
export const updateChannelSchema = z.enum(['stable', 'beta', 'internal']);
/** Публичное представление устройства для его собственного владельца. */
export const deviceSummarySchema = z.object({
    id: z.string(),
    status: deviceStatusSchema,
    platform: devicePlatformSchema,
    label: z.string(),
    model: z.string().nullable(),
    manufacturer: z.string().nullable(),
    osVersion: z.string().nullable(),
    appVersion: z.string().nullable(),
    abi: z.string().nullable(),
    channel: updateChannelSchema,
    userId: z.string().nullable(),
    username: z.string().nullable(),
    groups: z.array(z.string()),
    firstSeenAt: z.string(),
    lastSeenAt: z.string(),
    approvedAt: z.string().nullable(),
    revokedAt: z.string().nullable(),
    /**
     * Лёгкая версия портала: браузер этого устройства открывает `/lite/` вместо
     * обычного сайта. Ставит администратор (админка или бот); сам зритель её не
     * снимает. Сервер прежней версии поля не знает — тогда `false`.
     */
    lite: z.boolean().default(false),
    /**
     * Входит по одному одобрению, без пароля: устройство пришло через режим
     * ожидания. Обычному браузеру, кроме одобрения, нужен вход паролем.
     */
    passwordless: z.boolean().default(false),
});
/**
 * Режим ожидания устройства.
 *
 * Администратор включает его перед тем, как открыть портал на новом
 * устройстве. Пока режим включён, страница входа сама объявляет своё
 * устройство серверу — без логина и пароля: оно появляется у администратора
 * с кодом и ждёт одобрения. Первое же объявившееся устройство режим
 * выключает; не пришёл никто — он гаснет сам (`expiresAt`).
 */
export const deviceWaitingSchema = z.object({
    active: z.boolean(),
    /** Когда режим погаснет сам; `null` — выключен. */
    expiresAt: z.string().nullable(),
});
export const setDeviceWaitingRequestSchema = z.object({ active: z.boolean() });
/**
 * Объявление устройства со страницы входа — пока включён режим ожидания.
 *
 * `trigger` — что привело к объявлению, и без него сервер объявление не
 * принимает:
 * - `open` — портал открыли (или обновили страницу), пока администратор ждёт;
 * - `manual` — на странице, которая уже была открыта, человек сам нажал
 *   «Подключить это устройство».
 *
 * Страница, которая просто стоит открытой на форме входа, сама не объявляется
 * никогда: иначе режим ожидания доставался бы ей, а не устройству, ради
 * которого его включили (сбой на проде 06.10.2026).
 */
export const announceDeviceRequestSchema = z.object({
    trigger: z.enum(['open', 'manual']),
    label: z.string().trim().max(80).optional(),
    model: z.string().trim().max(80).optional(),
});
/** Включить или снять лёгкую версию у устройства (только администратор). */
export const setDeviceLiteRequestSchema = z.object({ lite: z.boolean() });
/**
 * Профиль на устройстве.
 *
 * Это пользователь, показанный так, как его выбирают с дивана: кружок с
 * аватаром и имя под ним. Пароля и роли здесь нет намеренно — экран выбора
 * профиля виден до всякой авторизации, и раскрывать через него состав ролей
 * незачем.
 */
export const deviceProfileSchema = z.object({
    id: z.string(),
    displayName: z.string(),
    /** Эмодзи для кружка; `null` — клиент рисует инициал. */
    avatar: z.string().max(8).nullable(),
    /** Загруженная картинка профиля; `null` — рисуем эмодзи или инициал. */
    avatarUrl: z.string().max(200).nullable().default(null),
    /** Владелец устройства: его нельзя отвязать, он же профиль по умолчанию. */
    owner: z.boolean(),
});
export const deviceProfilesResponseSchema = z.object({
    profiles: z.array(deviceProfileSchema),
    /** Кого выбрать, если зритель ничего не выбирал. */
    defaultProfileId: z.string().nullable(),
});
/**
 * Своё имя устройству. Пустая строка — вернуть то, которым устройство
 * представилось само: имя даёт человек, но лишать его исходного нельзя.
 */
export const renameDeviceRequestSchema = z.object({
    label: z.string().trim().max(80),
});
export const attachProfileRequestSchema = z.object({ userId: z.string().min(1).max(64) });
export const enrollRequestSchema = z.object({
    platform: devicePlatformSchema.default('web'),
    label: z.string().trim().max(80).optional(),
    model: z.string().trim().max(80).optional(),
    manufacturer: z.string().trim().max(80).optional(),
    osVersion: z.string().trim().max(40).optional(),
    appVersion: z.string().trim().max(40).optional(),
    abi: z.string().trim().max(40).optional(),
    /**
     * Публичный ключ устройства (base64). Приватный ключ остаётся на устройстве.
     * Сейчас сохраняется для будущего request proof и не используется как секрет.
     */
    publicKey: z.string().max(1024).optional(),
});
/** Код привязки: короткий, читаемый с экрана телевизора, без похожих символов. */
export const ENROLLMENT_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
export const ENROLLMENT_CODE_LENGTH = 8;
export const enrollmentCodeSchema = z
    .string()
    .trim()
    .toUpperCase()
    .regex(new RegExp(`^[${ENROLLMENT_CODE_ALPHABET}]{${ENROLLMENT_CODE_LENGTH}}$`), 'некорректный код привязки');
export const enrollResponseSchema = z.object({
    deviceId: z.string(),
    status: deviceStatusSchema,
    code: z.string(),
    codeExpiresAt: z.string(),
    /** Относительный путь QR-кода нашего домена, а не внешний генератор. */
    qrUrl: z.string().startsWith('/api/'),
    /** Путь страницы подтверждения, который зашит в QR. */
    verificationPath: z.string().startsWith('/'),
    pollIntervalMs: z.number().int().positive(),
    /** Persistent credential для не-браузерных клиентов; в браузере — cookie. */
    deviceToken: z.string().nullable(),
    message: z.string(),
});
export const enrollStatusResponseSchema = z.object({
    status: deviceStatusSchema,
    deviceId: z.string(),
    message: z.string(),
    pollIntervalMs: z.number().int().positive(),
});
/**
 * Подтверждение устройства уже авторизованным администратором.
 * Анонимное подтверждение по коду невозможно: код лишь адресует устройство.
 */
export const enrollApproveRequestSchema = z.object({
    /**
     * Кому отдать устройство. Обязателен, если владельца у устройства ещё нет:
     * одобривший администратор им молча не становится — устройство получило бы
     * его права. Если владелец уже есть (человек сам вошёл на устройстве), можно
     * не указывать.
     */
    userId: z.string().max(64).optional(),
});
//# sourceMappingURL=device.js.map